import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '@screentone/addon-auth-wrapper';
import { Box, Group, Tabs as TabsComponent, Typography, Wrapper } from '@screentone/core';
import { useQueryClient } from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';

import { CloneLayout } from 'components/artifact-settings/actions/CloneLayout';
import RegionToggle from 'components/artifact-settings/actions/RegionToggle';
import { ResetPageTypeSettings } from 'components/artifact-settings/actions/ResetPageTypeSettings';
import ScratchpadToggle from 'components/artifact-settings/actions/ScratchpadToggle';
import ArtifactSettings from 'components/artifact-settings/artifact-parameters/ArtifactParameters';
import ClientSectionParameters from 'components/artifact-settings/client-section-parameters/ClientSectionParameters';
import { PageTitle } from 'components/artifact-settings/page-title/PageTitle';
import { BehindFeatureFlag } from 'components/behind-feature-flag/BehindFeatureFlag';
import ExternalLink, { ExternalLinkContentType } from 'components/external-link/ExternalLink';
import { HistoryContent, PublishHistory } from 'components/publish-history/PublishHistory';
import { Container, EntityType } from 'contexts/datamodel/DataModelContext';
import { useDataModelContext } from 'contexts/datamodel/useDataModel';
import { usePageLock } from 'contexts/page-lock/usePageLock';
import { usePagePublish } from 'contexts/page-publish-dto/usePagePublish';
import { usePageTypeSettingsContext } from 'contexts/page-type-settings/usePageTypeSettings';
import {
  AvailableFeatureFlags,
  GroupConfig,
  ModuleContainerUnion,
  PageDto,
  SectionContainer,
  StructureGroupConfig,
  UiModuleType,
  UiTitleTreatmentType,
  useGetLiveContentByAllessehIdQuery
} from 'data/generated/graphql';
import { usePublicationSettings } from 'hooks/publication-settings';
import useIsNewSectionPage from 'hooks/useIsNewSectionPage';
import { PageDTOSubscriptionTopic, usePageDTOSubscription } from 'hooks/usePageDTOSubscription';
import { buildRootFromStructure } from 'pages/new-section-page/helpers';
import { convertPageModuleItemsToDataModelEntities } from 'utils/allesseh';
import { handleRegionToggle } from 'utils/regionToggle';
import { Tabs } from './pageMultitabSection.types';
import { ResetDefault } from '../../../../components/artifact-settings/actions/ResetDefault';
import { Settings } from '../../../../components/artifact-settings/Settings';
import pageEditStyles from '../../SectionPageEdit.module.scss';
import { PageRibbonButton, PageRibbonField, PageRibbonProvider } from '../page-ribbon/page-ribbon';
import { PageContentSearch } from '../page-search-section/page-content-search/PageContentSearch';
import { PageSearchSection } from '../page-search-section/PageSearchSection';

export { Tabs } from './pageMultitabSection.types';


interface PageMultitabSectionProps {
  onTabChange?: (tab: Tabs) => void;
}

interface PageSettingsContentProps {
  toggleableRegions: Array<StructureGroupConfig & { id: string }>;
  isRegionEnabled: Map<string, boolean>;
  handleGenericRegionToggle: (groupConfig: StructureGroupConfig & { id: string }, enabled: boolean) => void;
  handleResetToDefault: () => void;
  isReadOnly?: boolean;
}

const PageSettingsContent: FC<PageSettingsContentProps> = ({
  toggleableRegions,
  isRegionEnabled,
  handleGenericRegionToggle,
  handleResetToDefault,
  isReadOnly = false
}) => (
  <>
    <PageTitle isReadOnly={isReadOnly} />
    <Box>
      <Box.Title variant="header3">Enablements</Box.Title>
      <Box.Content>
        <Typography variant="header4">Module Enablement</Typography>
        <Group direction="column" gap="sm">
          <ScratchpadToggle />
          {toggleableRegions.map((groupConfig) => (
            <RegionToggle
              key={groupConfig.id}
              groupConfig={groupConfig}
              checked={isRegionEnabled.get(groupConfig.id) ?? groupConfig.defaultValue ?? false}
              onToggle={(enabled) => handleGenericRegionToggle(groupConfig, enabled)}
            />
          ))}
        </Group>
      </Box.Content>
    </Box>

    <BehindFeatureFlag featureFlagName={AvailableFeatureFlags.ShowRibbonMvp}>
      <Box>
        <Box.Title variant="header3">Page Ribbon</Box.Title>
        <Box.Content>
          <div>
            <PageRibbonProvider>
              <PageRibbonField>
                <PageRibbonButton />
              </PageRibbonField>
            </PageRibbonProvider>
          </div>
        </Box.Content>
      </Box>
    </BehindFeatureFlag>
    <ClientSectionParameters isReadOnly={isReadOnly} />
    <Box>
      <Box.Title>Page Layout</Box.Title>
      <Box.Content className={pageEditStyles.pageLayout}>
        <ResetDefault handleResetToDefault={handleResetToDefault} isReadOnly={isReadOnly} />
        <ResetPageTypeSettings isReadOnly={isReadOnly} />
        <CloneLayout isReadOnly={isReadOnly} />
      </Box.Content>
    </Box>
    <ArtifactSettings isReadOnly={isReadOnly} />
  </>
);

export const PageMultitabSection: FC<PageMultitabSectionProps> = ({ onTabChange }) => {
  const { publishUtc } = useParams();
  const [tabIndex, setTabIndex] = useState(Tabs.ContentSearch);
  const publishUtcParam = publishUtc ? parseInt(publishUtc, 10) : 0;
  const { data: publicationSettings } = usePublicationSettings();
  const {
    getDTO,
    insertEntity,
    removeAllByType,
    removeEntity,
    traverseEntity,
    getAllGroupConfigs,
    getRootCopy,
    setNewRoot
  } = useDataModelContext<SectionContainer>();
  const isCreatingNewPage = useIsNewSectionPage();
  const { state } = usePageLock();
  const publishingState = state.publishing;
  const { user } = useAuth();
  const queryClient = useQueryClient();

  if (isCreatingNewPage && tabIndex !== Tabs.Settings) {
    setTabIndex(Tabs.Settings);
  }

  const {
    hasRecentlyPublished,
    hasRecentlyScheduled,
    hasRecentlyPublishedScheduledContent,
    originalPageWhenInHistoryEditMode,
    tabToChangeTo,
    setTabToChangeTo,
    handleShowHistoryEditMode
  } = usePagePublish();
  const disableEmbargo = publicationSettings?.publicationSetting.disableEmbargo ?? false;
  const pageDTO = getDTO() as PageDto;
  const { allessehId, publicationKey } = pageDTO.metadata;
  const effectiveAllessehId =
    (publicationSettings?.publicationSetting.originalCollectionMapping as Record<string, string> | undefined)?.[allessehId] ?? allessehId;
  const [liveVersion, setLiveVersion] = useState<PageDto>();

  const handleScheduledPageCanceled = useCallback(() => {
    void queryClient.invalidateQueries(['GetScheduledContentByAllessehId']);
  }, [queryClient]);

  const scheduledPageCanceledParams = useMemo(
    () => ({
      allessehId,
      publicationKey,
      currentUserId: user?.email ?? '',
      onDeleted: handleScheduledPageCanceled,
      topic: 'scheduledPageCanceled' as PageDTOSubscriptionTopic
    }),
    [allessehId, publicationKey, user, handleScheduledPageCanceled]
  );

  usePageDTOSubscription(scheduledPageCanceledParams);
  const { getTreatmentTypeSetting, pageTypeSetting, currentLayout, structureTreatmentTypes } =
    usePageTypeSettingsContext();

  const { data, refetch } = useGetLiveContentByAllessehIdQuery(
    {
      allessehId,
      publicationKey
    },
    {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      enabled: !isCreatingNewPage && !!allessehId && !!publicationKey
    }
  );

  // TODO(SETTINGS_ONLY structure-migration): getAllGroupConfigs() returns [] for old-shape pages (no
  // structure on the layout artifact). Fall back to currentLayout.groupConfigs (GroupConfig[]) for
  // those pages — gate on currentLayout.structure presence to distinguish the two shapes.
  // Requires widening toggleableRegions / PageSettingsContent props / handleGenericRegionToggle from
  // StructureGroupConfig to the existing RegionGroupConfig union (regionToggle.ts).
  const toggleableRegions = useMemo(
    () => getAllGroupConfigs().filter((config) => config.isRightRailToggleable === true),
    [getAllGroupConfigs]
  );

  const isRegionEnabled = useMemo(() => {
    const pageCollection = pageDTO.root.collection[0]?.collection;
    const enabledGroups = new Map<string, boolean>();

    toggleableRegions.forEach((groupConfig) => {
      const hasEnabledModule = pageCollection.some(
        (item: ModuleContainerUnion) => item.groupId === groupConfig.id && item.isGroupEnabled === true
      );
      enabledGroups.set(groupConfig.id, hasEnabledModule);
    });

    return enabledGroups;
  }, [pageDTO, toggleableRegions]);

  const setTabIndexWrapper = (index: Tabs) => {
    setTabToChangeTo(Tabs.ContentSearch);
    setTabIndex(index);
    onTabChange?.(index);
  };

  useEffect(() => {
    if (data?.liveContentByAllessehId[0]?.body) {
      setLiveVersion(data.liveContentByAllessehId[0].body as PageDto);
    } else {
      setLiveVersion(pageDTO);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.liveContentByAllessehId]);

  useEffect(() => {
    if (hasRecentlyPublished && !hasRecentlyPublishedScheduledContent) {
      setLiveVersion(pageDTO);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasRecentlyPublished]);

  useEffect(() => {
    async function reset() {
      if (hasRecentlyPublishedScheduledContent) {
        await refetch();
      }
    }
    void reset();
  }, [hasRecentlyPublishedScheduledContent, refetch]);

  useEffect(() => {
    // Select Publish History tab when loading a shared page version
    if (publishUtc) {
      setTabIndexWrapper(Tabs.PublishHistory);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (tabToChangeTo && tabToChangeTo !== tabIndex) {
      setTabIndex(tabToChangeTo);
    }
  }, [tabToChangeTo, tabIndex]);

  const handleResetToDefault = () => {
    removeAllByType(EntityType.Module);

    // currentLayout comes from pageTypeSetting.layouts; the page-type query does not select
    // a structure field on layouts, so currentLayout?.structure is always undefined for structure
    // pages. Use the structure on the page DTO itself instead.
    if (pageDTO.structure) {
      const rootCopy = getRootCopy() as PageDto['root'];
      const existingLayoutId = (rootCopy.collection[0]?.attributes as { layoutId?: string } | undefined)?.layoutId;
      const layoutId = currentLayout?.id ?? existingLayoutId ?? '';
      const newRoot = buildRootFromStructure(
        rootCopy,
        pageDTO.structure,
        layoutId,
        structureTreatmentTypes as unknown as Parameters<typeof buildRootFromStructure>[3]
      );
      setNewRoot(newRoot as unknown as Container);
      return;
    }

    (currentLayout?.requiredLayoutModules ?? []).forEach((module, i) => {
      if (module.uiModuleType === UiModuleType.UiBasicModuleType && !!module.uiModuleFields?.basicModule) {
        // eslint-disable-next-line no-param-reassign
        module.uiModuleType = UiModuleType.UiTitleModuleType;
        // eslint-disable-next-line no-param-reassign
        module.uiModuleFields.titleModule = {
          ...module.uiModuleFields.basicModule,
          treatmentType: module.uiModuleFields.basicModule.treatmentType as UiTitleTreatmentType,
          allowedTreatmentTypes: module.uiModuleFields.basicModule.allowedTreatmentTypes as UiTitleTreatmentType[],
          __typename: 'UiTitleModule'
        };

        // eslint-disable-next-line no-param-reassign
        module.uiModuleFields.basicModule = null;
      }

      // TODO: Handle other module types, currently applicable to Feeds
      const collection = convertPageModuleItemsToDataModelEntities(module);

      const treatmentTypeSetting = getTreatmentTypeSetting(module);

      const newModule = {
        type: 'Module',
        uuid: uuidv4(),
        subtype: treatmentTypeSetting?.subtype,
        attributes: {
          pageModule: module,
          moduleId: module.moduleId,
          disableModuleIdEdit: module.disableModuleIdEdit,
          zones: treatmentTypeSetting?.zones,
          brand: treatmentTypeSetting?.brand
        },
        collection,
        groupId: module.groupId,
        isGroupEnabled: true
      };

      const legacyGroupConfigs = currentLayout?.groupConfigs as GroupConfig[] | undefined;
      const groupConfig =
        getAllGroupConfigs().find((g) => g.id === module.groupId) ??
        legacyGroupConfigs?.find((g) => g.id === module.groupId);

      if (groupConfig) {
        newModule.isGroupEnabled = groupConfig.defaultValue ?? false;
      }

      insertEntity(`0-0-${i}`, newModule);
    });
  };

  /**
   * Generic handler for toggling any region (group) on/off
   * Uses GroupConfig and TreatmentTypeSettings to determine what module to create
   */
  const handleGenericRegionToggle = (groupConfig: StructureGroupConfig & { id: string }, enabled: boolean) => {
    // SETTINGS_ONLY(structure-migration): treatmentTypeSettings is empty for structure pubs; fall back to structureTreatmentTypes.
    const settingsTreatmentTypes = pageTypeSetting?.treatmentTypeSettings ?? [];
    const treatmentTypeSettings = settingsTreatmentTypes.length > 0 ? settingsTreatmentTypes : structureTreatmentTypes;

    handleRegionToggle(
      enabled,
      {
        groupConfig,
        treatmentTypeSettings,
        pageDTO
      },
      insertEntity,
      removeEntity,
      traverseEntity
    );
  };

  if (isCreatingNewPage) {
    return (
      <Wrapper margin={{ top: 'mlg' }}>
        <Wrapper role="region" aria-label="Page Creation Settings" padding={{ top: 'sm' }}>
          <Settings title="Page Creation">
            <PageSettingsContent
              toggleableRegions={toggleableRegions}
              isRegionEnabled={isRegionEnabled}
              handleGenericRegionToggle={handleGenericRegionToggle}
              handleResetToDefault={handleResetToDefault}
            />
          </Settings>
        </Wrapper>
      </Wrapper>
    );
  }

  // For WSJ pages only — look up translation from the effective ID so that a redirected page
  // (e.g. UCSD_WSJ_MOBILE → UCSD_WSJ_) opens the correct translated collection.
  const translatedAllessehId: string | undefined = (
    publicationSettings?.publicationSetting.pubcrawlSectionIdMapping as Record<string, string> | undefined
  )?.[effectiveAllessehId];

  return (
    <Wrapper>
      <Group align="end" margin={{ bottom: 'lg' }} gap="sm">
        <ExternalLink
          contentId={effectiveAllessehId}
          additionalContentId={translatedAllessehId}
          debugContentId={allessehId}
          type={ExternalLinkContentType.COLLECTION}
          isPublished
        />
      </Group>
      <TabsComponent
        role="tablist"
        onChange={setTabIndexWrapper}
        value={tabIndex}
        margin={{ bottom: 'md' }}
        data-testid="page-multi-tab-list"
      >
        <TabsComponent.Item
          role="tab"
          id="tab-id0"
          aria-selected={tabIndex === Tabs.ContentSearch ? 'true' : 'false'}
          aria-controls="tabpanel-id0"
          data-testid="page-content-search-tab"
        >
          Content Search
        </TabsComponent.Item>
        <TabsComponent.Item
          role="tab"
          id="tab-id1"
          aria-selected={tabIndex === Tabs.Embargoed ? 'true' : 'false'}
          aria-controls="tabpanel-id1"
          disabled={disableEmbargo}
          data-testid="page-embargoed-tab"
        >
          Embargoed
        </TabsComponent.Item>
        <TabsComponent.Item
          role="tab"
          id="tab-id2"
          aria-selected={tabIndex === Tabs.PublishHistory ? 'true' : 'false'}
          aria-controls="tabpanel-id2"
          data-testid="page-publish-history-tab"
        >
          Publish History
        </TabsComponent.Item>
        <TabsComponent.Item
          role="tab"
          id="tab-id3"
          aria-selected={tabIndex === Tabs.Settings ? 'true' : 'false'}
          aria-controls="tabpanel-id3"
          data-testid="page-settings-tab"
        >
          Settings
        </TabsComponent.Item>
      </TabsComponent>
      {tabIndex === Tabs.ContentSearch && (
        <Wrapper
          id="tabpanel-id0"
          role="tabpanel"
          aria-labelledby="tab-id0"
          padding={{ top: 'sm' }}
          data-testid="page-multi-tab-section-content-search-tab-content"
        >
          <PageSearchSection showFilters={{ status: false }} showCollections={false} />
        </Wrapper>
      )}
      {tabIndex === Tabs.Embargoed && (
        <Wrapper
          id="tabpanel-id1"
          role="tabpanel"
          aria-labelledby="tab-id1"
          padding={{ top: 'sm' }}
          data-testid="page-multi-tab-section-embargoed-tab-content"
        >
          <Typography
            variant="header3"
            margin={{ bottom: 'none' }}
            data-testid="page-multi-tab-section-embargoed-tab-title"
          >
            Embargoed
          </Typography>
          <PageContentSearch embargo data-testid="page-multi-tab-section-embargoed-tab-list" />
        </Wrapper>
      )}
      {tabIndex === Tabs.PublishHistory && (
        <Wrapper id="tabpanel-id2" role="tabpanel" aria-labelledby="tab-id2" padding={{ top: 'sm' }}>
          <PublishHistory<PageDto>
            liveVersion={liveVersion}
            publishUtc={publishUtcParam}
            hasRecentlyPublished={hasRecentlyPublished || hasRecentlyScheduled}
            originalVersionWhenInHistoryEditMode={originalPageWhenInHistoryEditMode}
            currentlyEditedVersion={originalPageWhenInHistoryEditMode ? pageDTO : undefined}
            handleShowHistoryEditMode={(historyItem: HistoryContent) =>
              handleShowHistoryEditMode(historyItem as PageDto)
            }
            isReadOnly={publishingState.isReadOnly}
          />
        </Wrapper>
      )}
      {tabIndex === Tabs.Settings && (
        <Wrapper id="tabpanel-id3" role="tabpanel" aria-labelledby="tab-id3" padding={{ top: 'sm' }}>
          <Settings>
            <PageSettingsContent
              toggleableRegions={toggleableRegions}
              isRegionEnabled={isRegionEnabled}
              handleGenericRegionToggle={handleGenericRegionToggle}
              handleResetToDefault={handleResetToDefault}
              isReadOnly={publishingState.isReadOnly}
            />
          </Settings>
        </Wrapper>
      )}
    </Wrapper>
  );
};
